import { useState, useEffect } from "react";
import {
  X, Sparkles, ArrowLeftRight, Pencil, StickyNote, Trash2, Send, Save, Clock,
} from "lucide-react";
import { STAGES, SOURCES, ALL_TAGS } from "@/lib/constants";

const ACTIVITY_ICON = { created: Sparkles, stage: ArrowLeftRight, edit: Pencil, note: StickyNote };
const ACTIVITY_COLOR = { created: "#6EE7B7", stage: "#67E8F9", edit: "#FCD34D", note: "#C4B5FD" };

const fmtWhen = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
};

export default function ProspectDetailModal({ prospect, open, onClose, onSave, onAddNote, onDelete }) {
  const [form, setForm] = useState(prospect || {});
  const [saving, setSaving] = useState(false);
  const [note, setNote] = useState("");
  const [addingNote, setAddingNote] = useState(false);

  useEffect(() => {
    setForm(prospect || {});
  }, [prospect?.id]);

  if (!open || !prospect) return null;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleTag = (t) =>
    setForm((f) => ({
      ...f,
      tags: f.tags?.includes(t) ? f.tags.filter((x) => x !== t) : [...(f.tags || []), t],
    }));

  const save = async () => {
    setSaving(true);
    await onSave({
      name: form.name,
      email: form.email || "",
      phone: form.phone || "",
      company: form.company || "",
      stage: form.stage,
      monthly: Number(form.monthly) || 0,
      setup_fee: Number(form.setup_fee) || 0,
      source: form.source,
      tags: form.tags || [],
      notes: form.notes || "",
    });
    setSaving(false);
  };

  const submitNote = async (e) => {
    e.preventDefault();
    if (!note.trim()) return;
    setAddingNote(true);
    await onAddNote(note.trim());
    setNote("");
    setAddingNote(false);
  };

  const activity = [...(prospect.activity || [])].sort((a, b) => (b.at || "").localeCompare(a.at || ""));
  const inputCls =
    "w-full bg-[#0D1322] border border-white/10 rounded-xl px-3.5 py-2.5 text-white text-sm placeholder-slate-500 focus:outline-none focus:border-indigo-500/60 transition-colors";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="prospect-detail-modal">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div
        className="relative w-full max-w-3xl gos-card max-h-[90vh] overflow-hidden flex flex-col"
        style={{ background: "#0F2A47" }}
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-4 p-6 border-b border-white/8">
          <div className="min-w-0">
            <p className="eyebrow mb-1">Prospect Detail</p>
            <h2 className="font-display text-2xl font-bold text-white truncate">{form.name}</h2>
            {form.company && <p className="text-sm text-slate-400 truncate">{form.company}</p>}
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <button
              data-testid="detail-delete-btn"
              onClick={onDelete}
              className="p-2 rounded-lg text-slate-400 hover:text-red-400 hover:bg-white/5"
              title="Delete prospect"
            >
              <Trash2 className="w-4 h-4" />
            </button>
            <button
              data-testid="detail-close-btn"
              onClick={onClose}
              className="p-2 rounded-lg text-slate-400 hover:text-white hover:bg-white/5"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-0 overflow-hidden flex-1">
          {/* Left: editable fields */}
          <div className="p-6 space-y-3 overflow-y-auto border-r border-white/8">
            <p className="eyebrow">Edit Deal</p>
            <div>
              <label className="text-[11px] text-slate-400 mb-1.5 block">Full name</label>
              <input data-testid="detail-name-input" value={form.name || ""} onChange={(e) => set("name", e.target.value)} className={inputCls} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[11px] text-slate-400 mb-1.5 block">Email</label>
                <input data-testid="detail-email-input" value={form.email || ""} onChange={(e) => set("email", e.target.value)} className={inputCls} />
              </div>
              <div>
                <label className="text-[11px] text-slate-400 mb-1.5 block">Phone</label>
                <input data-testid="detail-phone-input" value={form.phone || ""} onChange={(e) => set("phone", e.target.value)} className={inputCls} />
              </div>
            </div>
            <div>
              <label className="text-[11px] text-slate-400 mb-1.5 block">Company / website</label>
              <input data-testid="detail-company-input" value={form.company || ""} onChange={(e) => set("company", e.target.value)} className={inputCls} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[11px] text-slate-400 mb-1.5 block">Stage</label>
                <select data-testid="detail-stage-select" value={form.stage} onChange={(e) => set("stage", e.target.value)} className={inputCls}>
                  {STAGES.map((s) => (
                    <option key={s.id} value={s.id} className="bg-[#11182B]">{s.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-[11px] text-slate-400 mb-1.5 block">Lead source</label>
                <select data-testid="detail-source-select" value={form.source} onChange={(e) => set("source", e.target.value)} className={inputCls}>
                  {SOURCES.map((s) => (
                    <option key={s} value={s} className="bg-[#11182B]">{s}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[11px] text-slate-400 mb-1.5 block">Setup fee ($)</label>
                <input data-testid="detail-setup-input" type="number" value={form.setup_fee ?? ""} onChange={(e) => set("setup_fee", e.target.value)} className={inputCls} />
              </div>
              <div>
                <label className="text-[11px] text-slate-400 mb-1.5 block">Retainer ($/mo)</label>
                <input data-testid="detail-monthly-input" type="number" value={form.monthly ?? ""} onChange={(e) => set("monthly", e.target.value)} className={inputCls} />
              </div>
            </div>
            <div>
              <label className="text-[11px] text-slate-400 mb-1.5 block">Tags</label>
              <div className="flex flex-wrap gap-2">
                {ALL_TAGS.map((t) => (
                  <button
                    key={t}
                    type="button"
                    data-testid={`detail-tag-${t.replace(/\s+/g, "-").toLowerCase()}`}
                    onClick={() => toggleTag(t)}
                    className={`text-xs px-2.5 py-1 rounded-lg border transition-colors ${
                      form.tags?.includes(t) ? "gos-gradient text-white border-transparent" : "border-white/10 text-slate-400 hover:text-white"
                    }`}
                  >
                    {t}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="text-[11px] text-slate-400 mb-1.5 block">Notes / pain points</label>
              <textarea data-testid="detail-notes-input" rows={3} value={form.notes || ""} onChange={(e) => set("notes", e.target.value)} className={inputCls + " resize-none"} />
            </div>
            <button
              data-testid="detail-save-btn"
              onClick={save}
              disabled={saving || !form.name?.trim()}
              className="w-full flex items-center justify-center gap-2 gos-gradient text-white font-semibold py-3 rounded-xl transition-transform hover:scale-[1.01] active:scale-[0.99] disabled:opacity-50"
            >
              <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save changes"}
            </button>
          </div>

          {/* Right: activity timeline */}
          <div className="p-6 flex flex-col overflow-hidden bg-[#0D1322]/40">
            <p className="eyebrow mb-4 flex items-center gap-1.5">
              <Clock className="w-3 h-3" /> Activity Timeline
            </p>
            <div className="flex-1 overflow-y-auto pr-1 space-y-4" data-testid="activity-timeline">
              {activity.length === 0 && <p className="text-sm text-slate-500">No activity yet.</p>}
              {activity.map((a, i) => {
                const Icon = ACTIVITY_ICON[a.type] || StickyNote;
                const color = ACTIVITY_COLOR[a.type] || "#94A3B8";
                return (
                  <div key={a.id || i} className="flex gap-3">
                    <div className="flex flex-col items-center">
                      <div className="w-7 h-7 rounded-full flex items-center justify-center shrink-0" style={{ background: `${color}22` }}>
                        <Icon className="w-3.5 h-3.5" style={{ color }} />
                      </div>
                      {i < activity.length - 1 && <div className="w-px flex-1 bg-white/8 my-1" />}
                    </div>
                    <div className="pb-1 min-w-0">
                      <p className="text-sm text-slate-200 break-words">{a.text}</p>
                      <p className="text-[11px] text-slate-500 mt-0.5 font-mono-g">{fmtWhen(a.at)}</p>
                    </div>
                  </div>
                );
              })}
            </div>
            <form onSubmit={submitNote} className="mt-4 flex items-center gap-2">
              <input
                data-testid="activity-note-input"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Log a note…"
                className="flex-1 bg-[#0D1322] border border-white/10 rounded-full px-4 py-2.5 text-white text-sm placeholder-slate-500 focus:outline-none focus:border-indigo-500/60"
              />
              <button
                data-testid="activity-note-submit"
                type="submit"
                disabled={addingNote || !note.trim()}
                className="p-2.5 rounded-full gos-gradient text-white disabled:opacity-50 transition-transform hover:scale-105"
              >
                <Send className="w-4 h-4" />
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
